import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Infobulle } from './Infobulle';
import { formaterHeure, formaterMontant, libelleJour } from '../lib/format';
import { libelleTitreTransport } from '../lib/contrat';
import type { BaseCalculee, NatureSalaire, PaiePersonnel } from '../types';

/** « 4,25 » heures hebdomadaires -> « 4 h 15 », exactes : c'est la saisie qui fait la paie. */
function heuresHebdo(heures: number): string {
  const minutes = Math.round(heures * 60);
  const h = Math.floor(minutes / 60);
  const min = minutes % 60;
  return min === 0 ? `${h} h` : `${h} h ${String(min).padStart(2, '0')}`;
}

/**
 * NET ou BRUT (2026-10-16) : tout montant de la paie est un net, sauf le
 * salaire fixe d'un administratif saisi en brut, qui est SOULIGNE — rien
 * d'autre, pour ne pas elargir les colonnes. Une premiere version posait
 * un point de couleur devant chaque montant ; retiree le jour meme, a la
 * demande. La legende dessous dit la regle.
 */
export function MontantNature({
  nature,
  children,
}: {
  nature: NatureSalaire | null | undefined;
  children: ReactNode;
}) {
  if (nature !== 'brut') return <>{children}</>;
  return (
    <Infobulle texte="Montant brut">
      <span className="underline decoration-encre-500 underline-offset-2">{children}</span>
    </Infobulle>
  );
}

export function LegendeNature({ classe = '' }: { classe?: string }) {
  return (
    <p className={`text-xs text-encre-400 ${classe}`}>
      Tous les montants sont exprimés en net. Les montants{' '}
      <span className="underline decoration-encre-500 underline-offset-2">soulignés</span> sont
      exprimés en brut.
    </p>
  );
}
import { basesDuRegime, heuresMinutes, type RegimePaie } from '../lib/paie';

/**
 * Les elements de paie d'UN salarie sur UN mois : le tableau de la modale
 * « Element de paie » de l'ecran Paie, sorti de la page le 2026-10-03
 * pour que la fiche du salarie le montre aussi. Deux ecrans, un seul
 * rendu — deux copies divergeraient.
 *
 * Le composant ne connait ni le selecteur de mois ni les actions de
 * saisie : la modale porte les siens, la fiche n'en a pas (consultation
 * seule). Il recoit le mois et, s'il y a lieu, les gestes de correction.
 */

/**
 * Le rendu a l'ecran, sur le modele de la tuile « Heures de cours » du
 * dashboard Activite : les unites en gris et plus petites que les
 * nombres — c'est le volume qu'on lit, pas la mention « h ».
 */
export function HeuresMinutes({ valeur }: { valeur: number }) {
  const { signe, h, mm } = heuresMinutes(valeur);
  const unite = 'text-xs font-normal text-encre-400';
  return (
    <>
      {signe}
      {h}
      <span className={unite}>h</span> {mm}
      <span className={unite}>min</span>
    </>
  );
}

/**
 * La ligne de tete : points d'anciennete, mutuelle et transport du mois.
 * Rend des fragments, l'appelant pose le conteneur — la modale y ajoute
 * son selecteur de mois a droite.
 */
export function ResumePaie({
  personnel,
  mode,
  mois,
  moisFiltre,
}: {
  personnel: PaiePersonnel;
  mode: RegimePaie;
  mois: string[];
  moisFiltre: string;
}) {
  const indexMois = mois.indexOf(moisFiltre);
  const mutuelleDuMois = personnel.details_mensuels[indexMois]?.mutuelle ?? 0;
  const transportDuMois = personnel.details_mensuels[indexMois]?.transport ?? 0;
  const titreDuMois = personnel.transports_mensuels[indexMois] ?? null;
  const figeDuMois = personnel.declarations_mensuelles[indexMois]?.figee ?? false;
  // Les points sont CEUX DU MOIS AFFICHE (0.71.0) : entiers, ils changent
  // au mois anniversaire de l'entree.
  const pointsDuMois = personnel.points_mensuels[indexMois] ?? 0;
  return (
    <>
      <span>
        <span className="tabulaire">{pointsDuMois}</span>{' '}
        {pointsDuMois === 1 ? 'point d’ancienneté' : 'points d’ancienneté'}
      </span>
      {/* Mutuelle et transport relevent du regime general : le cachet
          n'en porte pas, les afficher ici ferait croire l'inverse. */}
      {mode === 'general' && (
        <>
          {/* Le montant deduit sur le mois affiche : la cotisation vient
              des variables de paie de l'ecole, et vaut pour ce mois-la. */}
          <span>
            · mutuelle{' '}
            {personnel.mutuelle
              ? mutuelleDuMois > 0
                ? `souscrite ${formaterMontant(mutuelleDuMois)}/mois`
                : 'souscrite'
              : 'non souscrite'}
          </span>
          {/* Le titre du MOIS AFFICHE (2026-11-03) : les titres sont des
              periodes, le rembourse est celui que le moteur a retenu
              pour ce mois — figee sur un mois clos. La PART (2026-11-04)
              depend du temps de travail du mois : on la dit, avec les
              heures qui la fondent, sur un mois ouvert ; un mois clos
              porte ce qui a ete declare, le taux d'alors n'est pas
              conserve. */}
          <span>
            ·{' '}
            {titreDuMois
              ? `transport remboursé ${formaterMontant(transportDuMois)} (${
                  figeDuMois
                    ? 'déclaré à la clôture'
                    : `${titreDuMois.taux * 100} %, ${heuresHebdo(titreDuMois.heures_hebdo)} sur ${titreDuMois.base_hebdo} h`
                } — ${libelleTitreTransport(titreDuMois.type).toLowerCase()} ${formaterMontant(titreDuMois.montant)}/mois${
                  titreDuMois.justificatif ? '' : ', justificatif non fourni'
                })`
              : 'transport non remboursé'}
          </span>
        </>
      )}
    </>
  );
}

/** Le tableau d'un mois, ses totaux et ses notes. */
export function TableauElementsPaie({
  personnel,
  mode,
  moisFiltre,
  moisClotures,
  peutCorriger = false,
  onModifier,
  onSupprimer,
}: {
  personnel: PaiePersonnel;
  mode: RegimePaie;
  moisFiltre: string;
  moisClotures: string[];
  /** Les gestes de correction (`paie.corriger`) : la modale seule les propose. */
  peutCorriger?: boolean;
  onModifier?: (base: BaseCalculee) => void;
  onSupprimer?: (base: BaseCalculee) => void;
}) {
  const clotures = new Set(moisClotures);
  const bases = basesDuRegime(personnel, mode);
  // MOIS CLOS DECLARE (0.70.0) : la declaration dit ce qui a ete paye
  // dans le regime regarde ; les lignes du mois sont le detail CALCULE
  // AUJOURD'HUI depuis les inscriptions et le contrat. S'ils divergent,
  // l'ecart est dit ici, et le moteur le porte en regularisation sur le
  // premier mois ouvert.
  const declaration =
    personnel.declarations_mensuelles.find((d) => d.mois === moisFiltre && d.figee) ?? null;
  const declare = declaration
    ? mode === 'cachet'
      ? { heures: declaration.heures_cachet, montant: declaration.cachet, prime: declaration.prime_cachet }
      : { heures: declaration.heures, montant: declaration.salaire, prime: declaration.prime }
    : null;

  const libelleCreneau = (base: BaseCalculee) =>
    [
      `${libelleJour(base.creneau_jour)} ${formaterHeure(base.creneau_heure_debut)}` +
        (base.creneau_duree_minutes ? ` (${base.creneau_duree_minutes} min)` : ''),
      base.creneau_activite ?? '',
      base.creneau_eleve ?? '',
    ]
      .filter(Boolean)
      .join(' · ');

  /**
   * L'ordre de lecture : JOUR, HORAIRE, puis le LIBELLE par ordre
   * alphabetique — et le rang en dernier.
   *
   * Le libelle avant le rang n'est pas un detail : deux creneaux peuvent
   * partager un horaire (piano et trompette a 18h45), et trier par rang
   * d'abord poussait la regularisation SOUS L'AUTRE creneau. Trier par
   * libelle regroupe la mensualite et son solde, que le rang ordonne
   * ensuite — le solde juste sous la ligne qu'il regularise.
   *
   * Les ecritures MANUELLES restent a la fin : elles n'ont pas d'horaire,
   * les intercaler casserait la grille du planning qu'on suit des yeux.
   */
  const elementsDuMois = bases
    .filter((base) => base.mois === moisFiltre)
    .sort(
      (a, b) =>
        Number(a.creneau_id === null) - Number(b.creneau_id === null) ||
        (a.creneau_jour ?? 0) - (b.creneau_jour ?? 0) ||
        (a.creneau_heure_debut ?? '').localeCompare(b.creneau_heure_debut ?? '') ||
        libelleCreneau(a).localeCompare(libelleCreneau(b), 'fr') ||
        // Le libelle STOCKE ne departage que les ecritures manuelles.
        // L'appliquer aux lignes a creneau remontait la regularisation
        // AU-DESSUS de sa mensualite : « Regularisation » precede
        // « Trompette » dans l'alphabet, quand c'est le rang qui doit
        // trancher.
        (a.creneau_id === null ? (a.libelle ?? '').localeCompare(b.libelle ?? '', 'fr') : 0) ||
        a.rang - b.rang,
    );

  /** Ce que portent les lignes du mois affiche : les trois colonnes chiffrees. */
  const totauxDuMois = elementsDuMois.reduce(
    (cumul, base) => ({
      heures: cumul.heures + base.heures,
      montant: cumul.montant + base.montant,
      prime: cumul.prime + base.prime,
    }),
    { heures: 0, montant: 0, prime: 0 },
  );

  const quantite = (valeur: number) => valeur.toLocaleString('fr-FR');

  if (bases.length === 0 && !declare) {
    return (
      <p className="mb-3 rounded-md border border-dashed border-encre-200 px-3 py-2 text-sm text-encre-500">
        {mode === 'cachet'
          ? 'Aucun élément de paie au cachet.'
          : 'Aucun élément de paie : rien ne sera versé cette année.'}
      </p>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-encre-200 text-left text-xs uppercase tracking-wide text-encre-500">
            <th className="px-2 py-1.5">Créneau</th>
            {/* BASE : le nombre de seances de l'annee du creneau, en
                petit et en italique — pour CONTROLE, seances x duree x
                taux doit redonner le salaire annuel (2026-09-08). */}
            <th className="px-2 py-1.5 text-right">Base</th>
            <th className="px-2 py-1.5 text-right">Taux</th>
            <th className="px-2 py-1.5 text-right">Durée&nbsp;*</th>
            <th className="px-2 py-1.5 text-right">Salaire&nbsp;*</th>
            <th className="px-2 py-1.5 text-right">Prime anc.</th>
            {peutCorriger && <th className="px-2 py-1.5" />}
          </tr>
        </thead>
        <tbody>
          {/* MOIS CLOS : ce qui a ete declare, en tete — c'est lui qui a
              ete paye. Dessous, le detail calcule aujourd'hui depuis les
              inscriptions et le contrat (0.70.0) ; quand il diverge, on
              le dit, et l'ecart est regularise sur le premier mois
              ouvert. */}
          {declare && (
            <tr className="border-b border-encre-100 bg-encre-50">
              <td className="px-2 py-1.5">
                🔒 Déclaré à la clôture
                {Math.abs(totauxDuMois.montant - declare.montant) >= 0.005 ? (
                  <Infobulle texte="Le détail calculé aujourd’hui ne redonne pas le salaire déclaré : l’écart est régularisé sur le premier mois ouvert.">
                    <span className="ml-1.5 rounded bg-amber-100 px-1.5 py-0.5 text-[11px] text-amber-900">
                      calculé aujourd’hui :{' '}
                      {formaterMontant(Math.round((totauxDuMois.montant - declare.montant) * 100))}{' '}
                      d’écart
                    </span>
                  </Infobulle>
                ) : (
                  <span className="ml-1.5 text-xs text-encre-500">— détail calculé aujourd’hui ci-dessous</span>
                )}
              </td>
              <td className="px-2 py-1.5" />
              <td className="px-2 py-1.5" />
              <td className="tabulaire whitespace-nowrap px-2 py-1.5 text-right">
                <HeuresMinutes valeur={declare.heures} />
              </td>
              <td className="tabulaire px-2 py-1.5 text-right">
                <MontantNature
                  nature={personnel.statut === 'administratif' ? declaration?.nature_salaire : null}
                >
                  {formaterMontant(Math.round(declare.montant * 100))}
                </MontantNature>
              </td>
              <td className="tabulaire px-2 py-1.5 text-right">
                {declare.prime === 0 ? (
                  <span className="text-encre-300">—</span>
                ) : (
                  formaterMontant(Math.round(declare.prime * 100))
                )}
              </td>
              {peutCorriger && <td />}
            </tr>
          )}
          {!declare && elementsDuMois.length === 0 && (
            <tr>
              <td colSpan={peutCorriger ? 7 : 6} className="px-2 py-3 text-sm text-encre-400">
                Aucun élément de paie sur ce mois.
              </td>
            </tr>
          )}
          {elementsDuMois.map((base) => (
            <tr key={base.id} className="border-b border-encre-100">
              <td className="px-2 py-1.5">
                {base.creneau_id ? (
                  <Infobulle texte={base.libelle}>
                    <Link to="/creneaux" className="underline">
                      {libelleCreneau(base)}
                    </Link>
                  </Infobulle>
                ) : (
                  (base.libelle ?? <span className="text-encre-300">—</span>)
                )}
                {/* Une REGULARISATION porte le MEME libelle que la
                    ligne qu'elle solde : c'est le tag qui la
                    distingue, pas un prefixe qui decalerait la
                    lecture d'une colonne alignee. */}
                {base.rang === 2 && (
                  <Infobulle texte={base.libelle}>
                    <span className="ml-1.5 rounded bg-encre-100 px-1.5 py-0.5 text-[11px] text-encre-600">
                      régularisation
                    </span>
                  </Infobulle>
                )}
                {/* Le creneau ne couvre pas l'annee pleine : un eleve
                    est arrive apres la rentree, ou a arrete avant la
                    fin. La paie s'en trouve proratisee. Le solde, lui,
                    EST la consequence du prorata — l'en marquer aussi
                    serait redondant. */}
                {base.creneau_prorata && base.rang !== 2 && (
                  <Infobulle texte="Ce créneau porte un démarrage différé ou un arrêt anticipé : la paie est proratisée.">
                    <span className="ml-1.5 rounded bg-encre-100 px-1.5 py-0.5 text-[11px] text-encre-600">
                      prorata
                    </span>
                  </Infobulle>
                )}
                {/* Pas de tag « calcule » : le mois dit deja son etat
                    (cadenas ou non), le repeter sur chaque ligne etait
                    du bruit (retire le 2026-09-08). */}
              </td>
              <td className="tabulaire whitespace-nowrap px-2 py-1.5 text-right text-xs italic text-encre-400">
                {base.seances !== null ? (
                  <Infobulle texte={`${base.seances} séances dans l’année`}>{base.seances} cours</Infobulle>
                ) : (
                  ''
                )}
              </td>
              <td className="tabulaire px-2 py-1.5 text-right">
                {base.taux_horaire === 0 ? (
                  <span className="text-encre-300">—</span>
                ) : (
                  formaterMontant(base.taux_horaire)
                )}
              </td>
              <td className="tabulaire whitespace-nowrap px-2 py-1.5 text-right">
                {/* Duree EXACTE — pas d'arrondi au quart ici,
                    contrairement a la synthese : c'est la saisie qui
                    fait la paie. */}
                <Infobulle texte={`${quantite(base.heures)} h`}>
                  <HeuresMinutes valeur={base.heures} />
                </Infobulle>
              </td>
              <td className="tabulaire px-2 py-1.5 text-right">
                <MontantNature nature={personnel.statut === 'administratif' ? base.nature_montant : null}>
                  {formaterMontant(Math.round(base.montant * 100))}
                </MontantNature>
              </td>
              <td className="tabulaire px-2 py-1.5 text-right">
                {base.prime === 0 ? (
                  <span className="text-encre-300">—</span>
                ) : (
                  formaterMontant(Math.round(base.prime * 100))
                )}
              </td>
              {peutCorriger && (
                <td className="whitespace-nowrap px-2 py-1.5 text-right">
                  {/* Une ligne GENEREE appartient a la generation : elle
                      ne se corrige pas a la main, la prochaine passe
                      l'effacerait sans qu'on le voie. Pas de bouton du
                      tout — l'absence dit la regle, un mot de plus
                      serait du bruit sur 20 lignes. */}
                  {base.creneau_id !== null || base.calcule || clotures.has(base.mois) ? null : (
                    <>
                      <Infobulle texte="Modifier">
                        <button
                          type="button"
                          className="rounded px-1 text-encre-500 hover:bg-encre-100 hover:text-encre-800"
                          aria-label="Modifier"
                          onClick={() => onModifier?.(base)}
                        >
                          ✎
                        </button>
                      </Infobulle>
                      <Infobulle texte="Supprimer">
                        <button
                          type="button"
                          className="rounded px-1 text-encre-500 hover:bg-encre-100 hover:text-red-700"
                          aria-label="Supprimer"
                          onClick={() => onSupprimer?.(base)}
                        >
                          ✕
                        </button>
                      </Infobulle>
                    </>
                  )}
                </td>
              )}
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="border-t border-encre-300 font-medium">
            <td className="px-2 py-1.5">{declare ? 'Calculé aujourd’hui' : 'Totaux'}</td>
            <td className="px-2 py-1.5" />
            <td className="px-2 py-1.5" />
            <td className="tabulaire whitespace-nowrap px-2 py-1.5 text-right">
              <HeuresMinutes valeur={totauxDuMois.heures} />
            </td>
            <td className="tabulaire px-2 py-1.5 text-right">
              {formaterMontant(Math.round(totauxDuMois.montant * 100))}
            </td>
            <td className="tabulaire px-2 py-1.5 text-right">
              {totauxDuMois.prime === 0 ? (
                <span className="text-encre-300">—</span>
              ) : (
                formaterMontant(Math.round(totauxDuMois.prime * 100))
              )}
            </td>
            {peutCorriger && <td />}
          </tr>
        </tfoot>
      </table>

      {/* Ce que « moyen » veut dire : la duree et le salaire ne sont
          pas ceux du mois, ce sont ceux de l'annee ramenes au mois.
          Sans cette note, on cherche pourquoi un cours d'une heure
          affiche 2 h 30. */}
      <p className="mt-2 text-xs text-encre-400">
        * <strong>Durée</strong> et <strong>salaire</strong> sont des moyennes{' '}
        <strong>lissées</strong> : ce qui reste dû sur l’année (séances × durée × taux, moins
        ce qui est déjà clôturé), réparti à parts égales sur les mois ouverts restants. Ce ne
        sont pas les heures réellement faites ce mois-là.
        {elementsDuMois.some((b) => b.id.startsWith('salaire:')) && (
          <>
            {' '}
            Pour un <strong>salaire mensuel</strong> fixe, la durée est la durée de travail
            hebdomadaire mensualisée : heures par semaine × 52 ÷ 12.
          </>
        )}
      </p>

      {/* Ce que l'ecran NE dit PAS : le net reellement verse. Le CEA
          recalcule, et le laisser croire ferait prendre ces montants
          pour un bulletin. Au cachet, le CEA n'est pas en jeu (2026-10-07). */}
      {mode === 'general' && (
        <>
          <p className="mt-1 text-xs text-encre-400">
            Les montants affichés sont <strong>indicatifs</strong>, avant recalcul par le CEA.
          </p>
          {personnel.statut === 'administratif' && <LegendeNature classe="mt-1" />}
        </>
      )}
    </div>
  );
}
